import * as React from 'react'
import {useLocation, useNavigate, useParams} from 'react-router'
import {fromJust} from '@freckle/maybe'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'

import {type BasicSchoolWithRenaissanceClientT} from '@frontrow/console/src/models/school'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import {createDatatableUrl} from '@frontrow/console/src/helpers/data-table'
import EditSchool from '@frontrow/console/src/components/pages/edit-school-page/edit-school'
import {afterSave} from '@frontrow/console/src/components/common/unsaved-changes'
import {useSchool, useUpdateSchool} from '@frontrow/console/src/hooks/use-school'

function useSubmitForm(deps: {
  schoolId: number
  updateSchool: (
    schoolId: number,
    updateSchoolData: BasicSchoolWithRenaissanceClientT,
    shouldDeleteCleverAndClasslinkAssociations: boolean
  ) => Promise<unknown>
  schoolsUrl: () => string
}): (args: {
  updatedSchool: BasicSchoolWithRenaissanceClientT
  shouldAddFakeDistrict: boolean
  shouldDeleteCleverAndClasslinkAssociations: boolean
}) => void {
  const {schoolId, updateSchool, schoolsUrl} = deps
  const navigate = useNavigate()
  return (args: {
    updatedSchool: BasicSchoolWithRenaissanceClientT
    shouldAddFakeDistrict: boolean
    shouldDeleteCleverAndClasslinkAssociations: boolean
  }) => {
    const {updatedSchool, shouldDeleteCleverAndClasslinkAssociations} = args

    updateSchool(schoolId, updatedSchool, shouldDeleteCleverAndClasslinkAssociations)
      .then(() => navigate(schoolsUrl(), afterSave))
      .catch(() => {
        // useUpdateSchool shows the failure through its `error` state; rethrowing
        // here would only report a user error to Bugsnag as an unhandled rejection.
      })
  }
}

function useSchoolsUrl(): () => string {
  const location = useLocation()
  return () => {
    const state = location.state as {filters?: Array<{id: string; value: string}>} | null
    const referrerFilters = state && state.filters ? state.filters : []
    return createDatatableUrl('/schools', {filters: referrerFilters})
  }
}

export function EditSchoolContainer(): React.ReactElement {
  const params = useParams<{schoolId: string}>()
  const schoolId = parseInt(fromJust(params.schoolId, 'Cannot find schoolId in URL'), 10)

  const {school, isLoading, error} = useSchool(schoolId)
  const {updateSchool, error: updateError} = useUpdateSchool()

  const schoolsUrl = useSchoolsUrl()
  const submitForm = useSubmitForm({schoolId, schoolsUrl, updateSchool})

  const errors =
    (error !== null && error !== undefined) ||
    (updateError !== null && updateError !== undefined) ? (
      <ErrorAlert>{error ?? updateError}</ErrorAlert>
    ) : null

  const body =
    school !== null && school !== undefined ? (
      <EditSchool school={school} onSubmit={submitForm} schoolsUrl={schoolsUrl()} />
    ) : null

  return isLoading ? (
    <SpinnerOverlay />
  ) : (
    <div>
      {errors}
      {body}
    </div>
  )
}
